/**
 * SecretsCard Component
 *
 * Displays a list of secrets for an organization with permission-gated actions.
 * Users can view, edit, or create secrets based on their permissions.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    useSecretsForOrganization,
    useDeleteSecret,
    useDisableSecret,
    useEnableSecret,
    useCredentialUsages,
    useDisconnectCredentialUsages,
} from '@/lib/hooks/useSecrets';
import { SecretModal } from './SecretModal';
import { SecretActionButtons } from './SecretActionButtons';
import { CredentialCardView } from './CredentialCardView';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { authorizationApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { createComponentLogger, type ComponentLogger } from '@/lib/logging/frontend-logger';
import type {
    CredentialUsageListItem,
    CredentialUsageReference,
    ResourceDesignator,
    SecretIdentityReference,
    SecretListItem,
} from '@pillar-portal/api-types';
import {
    ThirdPartySystemId,
    ThirdPartySystemIdToName,
    resourceDesignatorKey,
} from '@pillar-portal/api-types';
import type { IsAuthorizedRequest } from '@pillar-portal/api-types';
import { AuthorizableAction, ResourceType } from '@pillar-portal/database/dist/frontend';
import { Plus } from 'lucide-react';

interface SecretsCardProps {
    organizationId?: number;
    organizationUuid?: string;
    organizationName: string;
}

function buildSecretsCardOrganizationLogContext(
    organizationId?: number,
    organizationUuid?: string
): Record<string, string | number> {
    const logContext: Record<string, string | number> = {};
    if (organizationId !== undefined) {
        logContext.organizationId = organizationId;
    }
    if (organizationUuid !== undefined) {
        logContext.organizationUuid = organizationUuid;
    }
    return logContext;
}

export function SecretsCard({
    organizationId,
    organizationUuid,
    organizationName,
}: SecretsCardProps) {
    const log: ComponentLogger = createComponentLogger('SecretsCard');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSecret, setSelectedSecret] = useState<SecretListItem | undefined>();
    const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [secretToDelete, setSecretToDelete] = useState<SecretListItem | null>(null);
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const [secretToDisconnect, setSecretToDisconnect] = useState<SecretListItem | null>(null);
    const [selectedUsageKeys, setSelectedUsageKeys] = useState<string[]>([]);

    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    const {
        data: canListAllSecretsWildcard,
        isError: isListAllSecretsWildcardAuthorizationError,
        error: listAllSecretsWildcardAuthorizationError,
    } = useQuery<boolean>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'is_authorized',
            AuthorizableAction.LIST,
            ResourceType.CREDENTIAL,
            organizationUuid === undefined
                ? 'no-organization'
                : resourceDesignatorKey({
                      resource_type: ResourceType.ORGANIZATION,
                      organization_uuid: organizationUuid,
                      descendants: false,
                  }),
        ]),
        queryFn: async () => {
            try {
                if (organizationUuid === undefined) {
                    return false;
                }
                const targetResource: ResourceDesignator = {
                    resource_type: ResourceType.ORGANIZATION,
                    organization_uuid: organizationUuid,
                    descendants: false,
                };
                const listAllSecretsWildcardRequest: IsAuthorizedRequest = {
                    action: AuthorizableAction.LIST,
                    resource_type: ResourceType.CREDENTIAL,
                    target_resource: targetResource,
                };
                const response = await authorizationApi.isAuthorized(listAllSecretsWildcardRequest);
                return response.is_authorized;
            } catch (authorizationError: unknown) {
                log.error(
                    'Failed to check secret LIST authorization',
                    authorizationError,
                    buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
                );
                throw authorizationError;
            }
        },
        enabled: organizationUuid !== undefined || organizationId !== undefined,
        retry: 0,
    });

    const { data: canCreateAllSecretsWildcard } = useQuery<boolean>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'is_authorized',
            AuthorizableAction.CREATE,
            ResourceType.CREDENTIAL,
            organizationUuid === undefined
                ? 'no-organization'
                : resourceDesignatorKey({
                      resource_type: ResourceType.ORGANIZATION,
                      organization_uuid: organizationUuid,
                      descendants: false,
                  }),
        ]),
        queryFn: async () => {
            try {
                if (organizationUuid === undefined) {
                    return false;
                }
                const targetResource: ResourceDesignator = {
                    resource_type: ResourceType.ORGANIZATION,
                    organization_uuid: organizationUuid,
                    descendants: false,
                };
                const createAllSecretsWildcardRequest: IsAuthorizedRequest = {
                    action: AuthorizableAction.CREATE,
                    resource_type: ResourceType.CREDENTIAL,
                    target_resource: targetResource,
                };
                const response = await authorizationApi.isAuthorized(createAllSecretsWildcardRequest);
                return response.is_authorized;
            } catch (authorizationError: unknown) {
                log.error(
                    'Failed to check secret CREATE authorization',
                    authorizationError,
                    buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
                );
                throw authorizationError;
            }
        },
        enabled:
            (organizationUuid !== undefined || organizationId !== undefined)
            && canListAllSecretsWildcard === true,
        retry: 0,
    });

    const organizationForSecretsQuery =
        canListAllSecretsWildcard === true && organizationUuid !== undefined
            ? {
                  organization_uuid: organizationUuid,
              }
            : undefined;
    const { data: secrets = [], isLoading, error } = useSecretsForOrganization(
        organizationForSecretsQuery
    );
    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();
    const credentialForDisconnectDialog: Pick<SecretIdentityReference, 'credential_uuid'> | undefined =
        disconnectDialogOpen && secretToDisconnect !== null
            ? {
                  credential_uuid: secretToDisconnect.credential_uuid,
              }
            : undefined;
    const {
        data: credentialUsagesResponse,
        isLoading: isCredentialUsagesLoading,
        error: credentialUsagesError,
    } = useCredentialUsages(credentialForDisconnectDialog);

    if (isListAllSecretsWildcardAuthorizationError) {
        const errorMessage: string = String(listAllSecretsWildcardAuthorizationError);
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Credentials</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>
                            Failed to check secrets permissions: {errorMessage}
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (canListAllSecretsWildcard !== true) {
        return null;
    }

    const handleCreate = () => {
        setSelectedSecret(undefined);
        setMode('create');
        setModalOpen(true);
    };

    const handleView = (secret: SecretListItem) => {
        setSelectedSecret(secret);
        setMode('view');
        setModalOpen(true);
    };

    const handleEdit = (secret: SecretListItem) => {
        setSelectedSecret(secret);
        setMode('edit');
        setModalOpen(true);
    };

    const handleDeleteClick = (secret: SecretListItem) => {
        setSecretToDelete(secret);
        setDeleteConfirmOpen(true);
    };

    const handleDisconnectClick = (secret: SecretListItem) => {
        setSecretToDisconnect(secret);
        setSelectedUsageKeys([]);
        setDisconnectDialogOpen(true);
    };

    const handleToggleUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ) => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedUsageKeys((previousSelectionKeys) =>
                previousSelectionKeys.includes(usageSelectionKey)
                    ? previousSelectionKeys
                    : [...previousSelectionKeys, usageSelectionKey]
            );
            return;
        }
        setSelectedUsageKeys((previousSelectionKeys) =>
            previousSelectionKeys.filter(
                (previousSelectionKey) => previousSelectionKey !== usageSelectionKey
            )
        );
    };

    const handleConfirmDisconnect = () => {
        if (secretToDisconnect === null) {
            log.error(
                'Disconnect confirmation clicked with no secret selected',
                undefined,
                buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
            );
            return;
        }
        const availableUsages: CredentialUsageListItem[] = credentialUsagesResponse?.usages ?? [];
        const selectedUsages: CredentialUsageReference[] = availableUsages
            .filter((usage) => selectedUsageKeys.includes(buildCredentialUsageSelectionKey(usage)))
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        const disconnectVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secretToDisconnect.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                      usages: selectedUsages,
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secretToDisconnect.credential_uuid,
                        },
                        organizationId,
                        usages: selectedUsages,
                    }
                  : undefined;
        if (disconnectVariables === undefined) {
            log.error('Disconnect requested without an organization reference', undefined, {
                credentialUuid: secretToDisconnect.credential_uuid,
            });
            return;
        }
        disconnectMutation.mutate(
            disconnectVariables,
            {
                onSuccess: () => {
                    setDisconnectDialogOpen(false);
                    setSecretToDisconnect(null);
                    setSelectedUsageKeys([]);
                },
                onError: (disconnectError: unknown) => {
                    log.error('Failed to disconnect credential usages', disconnectError, {
                        ...buildSecretsCardOrganizationLogContext(organizationId, organizationUuid),
                        credentialUuid: secretToDisconnect.credential_uuid,
                    });
                },
            }
        );
    };

    const handleDisable = (secret: SecretListItem) => {
        const disableVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secret.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secret.credential_uuid,
                        },
                        organizationId,
                    }
                  : undefined;
        if (disableVariables === undefined) {
            log.error('Disable requested without an organization reference', undefined, {
                credentialUuid: secret.credential_uuid,
            });
            return;
        }
        disableMutation.mutate(disableVariables);
    };

    const handleEnable = (secret: SecretListItem) => {
        const enableVariables =
            organizationUuid !== undefined
                ? {
                      secret: {
                          credential_uuid: secret.credential_uuid,
                      },
                      organization: {
                          organization_uuid: organizationUuid,
                      },
                  }
                : organizationId !== undefined
                  ? {
                        secret: {
                            credential_uuid: secret.credential_uuid,
                        },
                        organizationId,
                    }
                  : undefined;
        if (enableVariables === undefined) {
            log.error('Enable requested without an organization reference', undefined, {
                credentialUuid: secret.credential_uuid,
            });
            return;
        }
        enableMutation.mutate(enableVariables);
    };

    const handleConfirmDelete = () => {
        if (secretToDelete) {
            const deleteVariables =
                organizationUuid !== undefined
                    ? {
                          secret: {
                              credential_uuid: secretToDelete.credential_uuid,
                          },
                          organization: {
                              organization_uuid: organizationUuid,
                          },
                      }
                    : organizationId !== undefined
                      ? {
                            secret: {
                                credential_uuid: secretToDelete.credential_uuid,
                            },
                            organizationId,
                        }
                      : undefined;
            if (deleteVariables === undefined) {
                log.error('Delete requested without an organization reference', undefined, {
                    credentialUuid: secretToDelete.credential_uuid,
                });
                return;
            }
            deleteMutation.mutate(
                deleteVariables,
                {
                onSuccess: () => {
                    setDeleteConfirmOpen(false);
                    setSecretToDelete(null);
                },
                onError: (deleteError: unknown) => {
                    log.error('Failed to delete secret', deleteError, {
                        ...buildSecretsCardOrganizationLogContext(organizationId, organizationUuid),
                        credentialUuid: secretToDelete.credential_uuid,
                    });
                },
                }
            );
        } else {
            log.error(
                'Delete confirmation clicked with no secret selected',
                undefined,
                buildSecretsCardOrganizationLogContext(organizationId, organizationUuid)
            );
        }
    };

    const getSystemName = (systemId: ThirdPartySystemId): string => {
        const systemName = ThirdPartySystemIdToName[systemId];
        if (systemName) {
            return systemName
                .toLowerCase()
                .split('_')
                .map((segment) => {
                    if (segment.length <= 2) {
                        return segment.toUpperCase();
                    }
                    return segment.charAt(0).toUpperCase() + segment.slice(1);
                })
                .join(' ');
        }
        return `System ${systemId}`;
    };

    if (isLoading) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Credentials</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">Loading secrets...</p>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Credentials</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>
                            {error instanceof Error ? error.message : 'Failed to load secrets'}
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Credentials ({secrets.length})</CardTitle>
                    {canCreateAllSecretsWildcard === true ? (
                        <Button onClick={handleCreate} size="sm">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Credentials
                        </Button>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {deleteMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {deleteMutation.error instanceof Error
                                    ? deleteMutation.error.message
                                    : 'Failed to delete secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disableMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {disableMutation.error instanceof Error
                                    ? disableMutation.error.message
                                    : 'Failed to disable secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {enableMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {enableMutation.error instanceof Error
                                    ? enableMutation.error.message
                                    : 'Failed to enable secret'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disconnectMutation.error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertDescription>
                                {disconnectMutation.error instanceof Error
                                    ? disconnectMutation.error.message
                                    : 'Failed to disconnect credential usages'}
                            </AlertDescription>
                        </Alert>
                    )}

                    {secrets.length === 0 ? (
                        <p className="text-muted-foreground">
                            No secrets configured for this organization.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {secrets.map((secret) => (
                                <CredentialCardView
                                    key={secret.credential_uuid}
                                    credential={secret}
                                    showPublicFields={true}
                                    actions={
                        <SecretActionButtons
                            secret={secret}
                            onView={() => handleView(secret)}
                            onEdit={() => handleEdit(secret)}
                            onDisable={() => handleDisable(secret)}
                                            onEnable={() => handleEnable(secret)}
                                            onDelete={() => handleDeleteClick(secret)}
                                            onDisconnect={() => handleDisconnectClick(secret)}
                                        />
                                    }
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <SecretModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                organizationId={organizationId}
                organizationUuid={organizationUuid}
                organizationName={organizationName}
                secret={selectedSecret}
                mode={mode}
            />

            <ConfirmationModal
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                message={
                    secretToDelete
                        ? `Are you sure you want to delete the secret for ${getSystemName(secretToDelete.system_id)} (${secretToDelete.account_id})?`
                        : ''
                }
                onConfirm={handleConfirmDelete}
                confirmLabel="Delete"
                isLoading={deleteMutation.isPending}
            />

            <Dialog
                open={disconnectDialogOpen}
                onOpenChange={(isOpen) => {
                    setDisconnectDialogOpen(isOpen);
                    if (!isOpen) {
                        setSecretToDisconnect(null);
                        setSelectedUsageKeys([]);
                    }
                }}
            >
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>Disconnect Credential</DialogTitle>
                        <DialogDescription>
                            Select one or more usages to disconnect from this credential.
                        </DialogDescription>
                    </DialogHeader>

                    {isCredentialUsagesLoading ? (
                        <p className="text-sm text-muted-foreground">Loading credential usages...</p>
                    ) : credentialUsagesError ? (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {credentialUsagesError instanceof Error
                                    ? credentialUsagesError.message
                                    : 'Failed to load credential usages'}
                            </AlertDescription>
                        </Alert>
                    ) : (credentialUsagesResponse?.usages.length ?? 0) === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            This credential is not currently linked to any usage.
                        </p>
                    ) : (
                        <div className="space-y-3 max-h-72 overflow-auto pr-2">
                            {(credentialUsagesResponse?.usages ?? []).map((usage) => {
                                const usageSelectionKey: string =
                                    buildCredentialUsageSelectionKey(usage);
                                const isSelected: boolean = selectedUsageKeys.includes(
                                    usageSelectionKey
                                );
                                return (
                                    <label
                                        key={usageSelectionKey}
                                        className="flex items-start gap-3 rounded border p-3 cursor-pointer"
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked: boolean) =>
                                                handleToggleUsageSelection(usage, checked)
                                            }
                                        />
                                        <span className="text-sm">{usage.display_name}</span>
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDisconnectDialogOpen(false);
                                setSecretToDisconnect(null);
                                setSelectedUsageKeys([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDisconnect}
                            disabled={
                                selectedUsageKeys.length === 0 || disconnectMutation.isPending
                            }
                        >
                            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
